/**
 * API configuration utilities for frontend
 * Provides consistent API base URL resolution across the application
 */

/**
 * Normalize API base URL by removing trailing slashes
 * @param {string} value - The URL to normalize
 * @returns {string} Normalized URL without trailing slashes
 */
export const normalizeApiBaseUrl = (value) => String(value || '').replace(/\/+$/, '');

/**
 * Get the API base URL based on environment
 * Checks VITE_API_URL environment variable, or uses current origin (non-localhost),
 * or falls back to localhost:5000
 * @returns {string} The API base URL
 */
export const getApiBaseUrl = () => {
  return normalizeApiBaseUrl(
    import.meta.env.VITE_API_URL ||
    (typeof window !== 'undefined' && window.location.hostname !== 'localhost'
      ? `${window.location.origin}/api`
      : 'http://localhost:5000/api')
  );
};

/**
 * Get the base URL without the /api suffix
 * Useful for direct file uploads and other non-API requests
 * @returns {string} The base URL without /api suffix
 */
export const getBaseUrl = () => {
  const apiBase = getApiBaseUrl();
  return apiBase.replace(/\/api$/, '');
};

/**
 * Construct a full URL for an API endpoint
 * @param {string} endpoint - The API endpoint path (e.g., '/projects')
 * @returns {string} Full URL for the endpoint
 */
export const getApiUrl = (endpoint) => {
  const base = getApiBaseUrl();
  const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${base}${path}`;
};

/**
 * Construct a full URL for a file/resource
 * @param {string} filePath - The file path relative to uploads
 * @returns {string} Full URL for the file
 */
export const getFileUrl = (filePath) => {
  if (!filePath) return '';
  const base = getBaseUrl();
  const normalizedPath = filePath.startsWith('/') ? filePath : `/${filePath}`;
  return `${base}${normalizedPath}`;
};

// Export the default API base URL for backward compatibility
export const API_BASE_URL = getApiBaseUrl();
export const BASE_URL = getBaseUrl();

// Also export common named export for api.js compatibility
export default getApiBaseUrl;
